import React, { useState, useEffect, useCallback } from 'react';
import { LobbyClient } from 'boardgame.io/client';
import { useTranslation } from '../i18n/index.js';
import BottomButtons from '../components/BottomButtons.jsx';
import './Lobby.css';

const GAME_NAME = 'waterloo';

export default function Lobby({ serverUrl, onJoinedMatch }) {
  const { t } = useTranslation();
  const [playerName, setPlayerName] = useState(() =>
    localStorage.getItem('waterloo-name') || ''
  );
  const [view, setView] = useState('name-entry');
  const [numPlayers, setNumPlayers] = useState(2);
  const [matchID, setMatchID] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [playerID, setPlayerID] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [players, setPlayers] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const lobbyClient = new LobbyClient({ server: serverUrl });

  const saveName = () => {
    if (playerName.trim()) {
      localStorage.setItem('waterloo-name', playerName.trim());
      setView('menu');
    }
  };

  const createMatch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { matchID: newMatchID } = await lobbyClient.createMatch(GAME_NAME, {
        numPlayers,
      });

      const { playerID: newPlayerID, playerCredentials } = await lobbyClient.joinMatch(
        GAME_NAME,
        newMatchID,
        { playerName: playerName.trim() }
      );

      setMatchID(newMatchID);
      setPlayerID(newPlayerID);
      setCredentials(playerCredentials);
      setView('waiting');
    } catch (err) {
      setError(t('error.createFailed', { message: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  const joinMatch = async () => {
    if (!joinCode.trim()) {
      setError(t('error.enterMatchCode'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const match = await lobbyClient.getMatch(GAME_NAME, joinCode.trim());

      const availableSlot = match.players.find(p => !p.name);
      if (!availableSlot) {
        setError(t('error.matchFull'));
        return;
      }

      const { playerID: newPlayerID, playerCredentials } = await lobbyClient.joinMatch(
        GAME_NAME,
        joinCode.trim(),
        { playerName: playerName.trim() }
      );

      setMatchID(joinCode.trim());
      setPlayerID(newPlayerID);
      setCredentials(playerCredentials);
      setNumPlayers(match.players.length);
      setView('waiting');
    } catch (err) {
      setError(t('error.joinFailed', { message: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for player updates in waiting room
  useEffect(() => {
    if (view !== 'waiting' || !matchID) return;

    const pollPlayers = async () => {
      try {
        const match = await lobbyClient.getMatch(GAME_NAME, matchID);
        setPlayers(match.players);

        // Check if all players have joined
        const allJoined = match.players.every(p => p.name);
        if (allJoined) {
          // Build playerNames map from match data
          const playerNames = {};
          match.players.forEach((p, i) => {
            playerNames[String(i)] = p.name || `Player ${i}`;
          });
          onJoinedMatch({
            matchID,
            playerID,
            credentials,
            numPlayers: match.players.length,
            playerNames,
          });
        }
      } catch (err) {
        console.error('Failed to poll match:', err);
      }
    };

    pollPlayers();
    const interval = setInterval(pollPlayers, 2000);
    return () => clearInterval(interval);
  }, [view, matchID, playerID, credentials, onJoinedMatch]);

  const copyMatchCode = useCallback(() => {
    navigator.clipboard.writeText(matchID);
    setCopied('code');
    setTimeout(() => setCopied(null), 1500);
  }, [matchID]);

  const copyMatchLink = useCallback(() => {
    const url = `${window.location.origin}/online.html?match=${matchID}`;
    navigator.clipboard.writeText(url);
    setCopied('link');
    setTimeout(() => setCopied(null), 1500);
  }, [matchID]);

  // Check for match code in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const matchParam = params.get('match');
    if (matchParam) {
      setJoinCode(matchParam);
      if (playerName.trim()) {
        setView('join');
      }
    }
  }, []);

  if (view === 'name-entry') {
    return (
      <div className="lobby">
        <div className="lobby-card">
          <h1>{t('lobby.title')}</h1>
          <p>{t('lobby.enterName')}</p>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t('lobby.yourName')}
            maxLength={20}
            onKeyDown={(e) => e.key === 'Enter' && saveName()}
            autoFocus
          />
          <button onClick={saveName} disabled={!playerName.trim()}>
            {t('button.continue')}
          </button>
        </div>
        <BottomButtons />
      </div>
    );
  }

  if (view === 'menu') {
    return (
      <div className="lobby">
        <div className="lobby-card">
          <h1>{t('lobby.title')}</h1>
          <p className="welcome">{t('lobby.welcome', { name: playerName })}</p>
          <div className="menu-buttons">
            <button onClick={() => setView('create')}>{t('lobby.createMatch')}</button>
            <button onClick={() => setView('join')}>{t('lobby.joinMatch')}</button>
          </div>
          <button className="change-name" onClick={() => setView('name-entry')}>
            {t('button.changeName')}
          </button>
        </div>
        <BottomButtons />
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="lobby">
        <div className="lobby-card">
          <h1>{t('lobby.createMatch')}</h1>
          <label>
            {t('lobby.numPlayers')}
            <select value={numPlayers} onChange={(e) => setNumPlayers(Number(e.target.value))}>
              <option value={2}>{t('lobby.players2')}</option>
              <option value={3}>{t('lobby.players3')}</option>
              <option value={4}>{t('lobby.players4')}</option>
            </select>
          </label>
          {error && <p className="error">{error}</p>}
          <div className="button-row">
            <button className="secondary" onClick={() => setView('menu')}>{t('button.back')}</button>
            <button onClick={createMatch} disabled={isLoading}>
              {isLoading ? t('button.creating') : t('button.create')}
            </button>
          </div>
        </div>
        <BottomButtons />
      </div>
    );
  }

  if (view === 'join') {
    return (
      <div className="lobby">
        <div className="lobby-card">
          <h1>{t('lobby.joinMatch')}</h1>
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t('lobby.matchCode')}
            onKeyDown={(e) => e.key === 'Enter' && joinMatch()}
            autoFocus
          />
          {error && <p className="error">{error}</p>}
          <div className="button-row">
            <button className="secondary" onClick={() => setView('menu')}>{t('button.back')}</button>
            <button onClick={joinMatch} disabled={isLoading || !joinCode.trim()}>
              {isLoading ? t('button.joining') : t('button.join')}
            </button>
          </div>
        </div>
        <BottomButtons />
      </div>
    );
  }

  if (view === 'waiting') {
    const joinedCount = players.filter(p => p.name).length;
    const totalSlots = players.length;

    return (
      <div className="lobby">
        <div className="lobby-card waiting-room">
          <h1>{t('lobby.waitingRoom')}</h1>

          <div className="match-code">
            <span>{t('lobby.matchCodeLabel')}</span>
            <code>{matchID}</code>
            <div className="copy-buttons">
              <button className="small" onClick={copyMatchCode}>
                {copied === 'code' ? t('button.copied') : t('button.copyCode')}
              </button>
              <button className="small" onClick={copyMatchLink}>
                {copied === 'link' ? t('button.copied') : t('button.copyLink')}
              </button>
            </div>
          </div>

          <div className="players-list">
            <h3>{t('lobby.playersCount', { joined: joinedCount, total: totalSlots })}</h3>
            {players.map((player, idx) => (
              <div key={idx} className={`player-slot ${player.name ? 'filled' : 'empty'}`}>
                <span className="player-number">{t('lobby.playerSlot', { number: idx + 1 })}</span>
                <span className="player-name">
                  {player.name || t('lobby.waiting')}
                  {player.id === Number(playerID) && ` ${t('lobby.you')}`}
                </span>
              </div>
            ))}
          </div>

          <p className="waiting-hint">
            {t('lobby.autoStart')}
          </p>
        </div>
        <BottomButtons />
      </div>
    );
  }

  return null;
}
